/**
 * Pure art-slot configuration and fallback decisions — no DOM, no `Image`, testable
 * without a browser (same pattern as `../params`/`../url-guard`). The actual image
 * loading/caching (which DOES need the DOM) lives in `./artLoader`; `./canvas` composes
 * the two: resolve a URL here, kick off a load there, and only ever draw the loaded
 * image once `shouldUseFallback` says it's ready — never block a frame on a fetch, and
 * never show a blank/broken board while art is missing or still loading.
 *
 * Precedence for the art base URL (`resolveArtBaseUrl`): `?artBase=` (sanitized by
 * `sanitizeArtBaseParam` below) > the build-time R2 public URL constant (injected via
 * Vite's `define`, read only by `../main` — see `./env.d.ts`) > same-origin `/art/`.
 * This intentionally does NOT restrict the base URL to same-origin the way
 * `../url-guard`'s `sameOriginUrl` does for `?audioUrl=` — the whole point of the
 * build-time constant is to point at a DIFFERENT origin (the R2 bucket's own public
 * URL), and `?artBase=` exists to override that same choice at runtime for testing.
 */

export type ArtLoadState = "loading" | "ready" | "error";

const DEFAULT_ART_BASE_PATH = "/art/";
const WALL_TEXTURE_RELATIVE_PATH = "wall.png";

function withTrailingSlash(url: string): string {
  return url.endsWith("/") ? url : `${url}/`;
}

/**
 * Accepts only an absolute http(s) URL or a root-relative path ("/..."). Rejects
 * anything else (e.g. `javascript:`, `data:`, a bare relative path with no leading
 * "/", empty/whitespace) — a query string is untrusted input and this value becomes an
 * `<img>`/`Image().src`. Returns `undefined` (never throws) on anything unacceptable,
 * same silently-ignored-on-invalid convention as `../params`.
 */
export function sanitizeArtBaseParam(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  if (trimmed.startsWith("/")) return trimmed;
  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:" ? trimmed : undefined;
  } catch {
    return undefined;
  }
}

export interface ResolveArtBaseUrlOptions {
  /** Raw `?artBase=` value (unsanitized — this function sanitizes it itself). */
  readonly artBaseParam: string | undefined;
  /** The build-time R2 public URL constant, or `undefined` when unset/blank — see
   * `./env.d.ts` and `../main`'s read of `__SCARYMAZE_ART_BASE_URL__`. */
  readonly buildTimeBaseUrl: string | undefined;
}

/** Resolves the effective art base URL. Always returns a URL ending in "/", so callers
 * can join a relative path onto it with plain string concatenation. */
export function resolveArtBaseUrl(opts: ResolveArtBaseUrlOptions): string {
  const fromParam = sanitizeArtBaseParam(opts.artBaseParam);
  const buildTime =
    opts.buildTimeBaseUrl !== undefined && opts.buildTimeBaseUrl.trim() !== ""
      ? opts.buildTimeBaseUrl.trim()
      : undefined;
  return withTrailingSlash(fromParam ?? buildTime ?? DEFAULT_ART_BASE_PATH);
}

/** Joins `baseUrl` (assumed to already end in "/", as `resolveArtBaseUrl` guarantees)
 * with a relative asset path. A leading "/" on `relativePath` is stripped first, so a
 * path can be written either way without producing a doubled slash. */
export function resolveAssetUrl(baseUrl: string, relativePath: string): string {
  const base = withTrailingSlash(baseUrl);
  const rel = relativePath.startsWith("/") ? relativePath.slice(1) : relativePath;
  return `${base}${rel}`;
}

/** The relative path (under the art base URL) for a level's backdrop, keyed by its
 * `theme` (see `src/types.ts`'s `LevelTheme`) rather than its numeric id — a level
 * redesign that keeps the same theme keeps the same backdrop. */
export function levelBackdropPath(theme: string): string {
  return `levels/${theme}.png`;
}

/** The relative path (under the art base URL) for the board's single wall texture —
 * shared by every level, unlike the per-level backdrop above. */
export function wallTexturePath(): string {
  return WALL_TEXTURE_RELATIVE_PATH;
}

/**
 * Whether the renderer should fall back to today's coded look for an asset whose cache
 * state is `state` (`undefined` when the URL was never even requested yet). Only a
 * state of exactly `"ready"` unlocks using the loaded image — `"loading"` and `"error"`
 * (and the missing/never-requested case) all fall back, so a slow or failed fetch can
 * never leave a frame with a blank or half-drawn board.
 */
export function shouldUseFallback(state: ArtLoadState | undefined): boolean {
  return state !== "ready";
}
